import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type ToastKind = "success" | "error" | "info";
type Toast = { id: number; kind: ToastKind; text: string };

type Ctx = {
  push: (text: string, kind?: ToastKind) => void;
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
};

const ToastCtx = createContext<Ctx | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((text: string, kind: ToastKind = "info") => {
    const id = nextId++;
    setToasts((ts) => [...ts, { id, kind, text }]);
    setTimeout(() => {
      setToasts((ts) => ts.filter((t) => t.id !== id));
    }, 3800);
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      push,
      success: (t) => push(t, "success"),
      error: (t) => push(t, "error"),
      info: (t) => push(t, "info"),
    }),
    [push]
  );

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="fixed bottom-5 right-5 z-[70] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm text-sm animate-[fadeIn_150ms_ease-out] ${
              t.kind === "success"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
                : t.kind === "error"
                ? "bg-rose-500/10 border-rose-500/30 text-rose-100"
                : "bg-ink-800/90 border-white/10 text-slate-100"
            }`}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Fall back to a no-op rather than crashing if a test renders a component outside the provider.
    return {
      push: () => undefined,
      success: () => undefined,
      error: () => undefined,
      info: () => undefined,
    };
  }
  return ctx;
}
